import React, {
  memo, useCallback, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  func,
} from 'prop-types';
import concat from 'lodash/concat';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import Alert from '@active/react-ui/Alert';
import Input from '@active/react-ui/Input';
import Form, { FormConsumer } from '@active/react-ui/Form';
import Locale from '@active/react-utils/i18n/Locale';
import Grid from '@active/react-ui/Grid';
import GridItem from '@active/react-ui/GridItem';
import Button from '@active/react-ui/Button';
import FormLabel from '@active/react-ui/FormLabel';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import errorMap from './errorMap';
import { useApplyCouponMutation } from '../../../store/services/checkout/checkoutApi';
import pubSubInstance from '../../../shared/utils/pubSub.js';

const Coupon = ({
  i18n,
  trackingCallback,
}) => {
  const [ applyCoupon ] = useApplyCouponMutation();

  const {
    entities: {
      orderInfo: {
        couponCodes: orderCouponCodes = [],
        identifier: checkoutOrderIdentifier,
      },
    },
  } = useSelector(state => state.mainStateData);

  const {
    widgetNameSpace,
  } = useSelector(state => state.propsData);

  const [ appliedCouponCodesList, setAppliedCouponCodesList ] = useState(orderCouponCodes);
  const [ infoMsg, setInfoMsg ] = useState('');
  let couponCodeForm;

  const handleSubmit = useCallback(({
    couponCode,
  }) => {
    setInfoMsg('');
    if (!couponCode) return;
    if (appliedCouponCodesList.indexOf(couponCode.toUpperCase()) > -1) {
      setInfoMsg(errorMap.ERROR);
      return;
    }

    trackingCallback && trackingCallback({ id: 'cui.coupon.apply' });
    const couponCodes = concat([ couponCode ], appliedCouponCodesList);
    applyCoupon({
      params: { couponCodes },
    }).unwrap().then((
      { appliedCouponCodes = [], repriceRequired },
    ) => {
      setAppliedCouponCodesList(appliedCouponCodes);
      sessionStorage.setItem(`checkoutCoupon_${checkoutOrderIdentifier}`, JSON.stringify(appliedCouponCodes));
      if (appliedCouponCodes.indexOf(couponCode.toUpperCase()) > -1) {
        setInfoMsg(errorMap.INFO);
        couponCodeForm.setFieldValue('couponCode', '');// clear input after apply success
      } else {
        setInfoMsg(errorMap.ERROR);
      }
      if (repriceRequired) {
        pubSubInstance.dispatch(`${widgetNameSpace}-reload`, { isReloadOffer: true });
      }
    }).catch(() => {
      setInfoMsg(errorMap.SERVERERROR);
    });
  }, [
    applyCoupon,
    trackingCallback,
    checkoutOrderIdentifier,
    appliedCouponCodesList,
    setAppliedCouponCodesList,
    setInfoMsg,
  ]);

  const handleInputChanged = useCallback((e) => {
    if (!e.target.value) {
      setInfoMsg('');
    }
  }, []);

  return (
    <div className="coupon-code">
      <Form
        onSubmit={handleSubmit}
        customizeLayout
        validateOnBlur={false}>
        <Grid colGap={0} rowGap={0}>
          <GridItem span={12}>
            <div className="coupon-code__item">
              <div className="coupon-code__input">
                <FormGroup name="couponCode" className="coupon-code__input-field">
                  <FormLabel hidden>
                    <Locale id="checkout.lib.components.text.coupon" />
                  </FormLabel>
                  <FormField
                    size="sm"
                    maxLength="255"
                    autoComplete="off"
                    placeholder={i18n('checkout.lib.components.text.coupon')}
                    onChange={handleInputChanged}
                    as={Input}/>
                </FormGroup>
              </div>
              <Button submit type="secondary" size="sm" className="coupon-code__button cui-mg-l-8">
                <Locale id="checkout.lib.components.common.btn.apply"/>
              </Button>
            </div>
            {infoMsg && <Alert theme="inline"><Locale id={infoMsg} /></Alert>}
            {orderCouponCodes.length > 0
            && <div className="coupon-code__coupon-list">
              {
                orderCouponCodes.map((item, index) => {
                  return <div key={index} className="coupon-code__coupon-item">{item}</div>;
                })
              }
            </div>
            }
          </GridItem>
        </Grid>
        <FormConsumer>
          {
            ({
              form,
            }) => {
              couponCodeForm = form;
            }
          }
        </FormConsumer>
      </Form>
    </div>
  );
};

Coupon.propTypes = {
  i18n: func.isRequired,
  trackingCallback: func,
};

Coupon.defaultProps = {
  trackingCallback: undefined,
};

export default withI18n(memo(Coupon));
